import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/providers/AuthProvider';
import type { Database } from '@/types/database';
import type { Task, TaskWithClient } from './types';

type TaskInsert = Database['public']['Tables']['tasks']['Insert'];
type TaskUpdate = Database['public']['Tables']['tasks']['Update'];

interface ClientJoin {
  name: string | null;
}

interface TaskRowWithClientJoin extends Task {
  client?: ClientJoin | ClientJoin[] | null;
}

const flattenClient = (row: TaskRowWithClientJoin): TaskWithClient => {
  const client = row.client;
  const clientObj = Array.isArray(client) ? client[0] : client;
  const { client: _omit, ...rest } = row;
  void _omit;
  return { ...rest, client_name: clientObj?.name ?? null };
};

/**
 * Tarefas do consultor logado (assigned_to = me) + tarefas que ele criou.
 * Inclui o nome do cliente quando há client_id.
 */
export function useMyTasks() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['tasks', 'mine', user?.id],
    enabled: !!user?.id,
    staleTime: 30_000,
    refetchInterval: 60_000,
    queryFn: async (): Promise<TaskWithClient[]> => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from('tasks')
        .select('*, client:clients(name)')
        .is('deleted_at', null)
        .or(`assigned_to_user_id.eq.${user.id},created_by_user_id.eq.${user.id}`)
        .order('due_at', { ascending: true, nullsFirst: false })
        .limit(500);
      if (error) throw error;
      return (data as TaskRowWithClientJoin[]).map(flattenClient);
    },
  });
}

/**
 * Todas as tarefas vivas — usada no dashboard do gerente. A RLS da tabela
 * `tasks` já libera SELECT total para admin_clinance/gerente_operacao; para
 * outros roles o retorno fica restrito ao que a policy permite.
 */
export function useAllTasks() {
  return useQuery({
    queryKey: ['tasks', 'all'],
    staleTime: 30_000,
    refetchInterval: 60_000,
    queryFn: async (): Promise<TaskWithClient[]> => {
      const { data, error } = await supabase
        .from('tasks')
        .select('*, client:clients(name)')
        .is('deleted_at', null)
        .order('due_at', { ascending: true, nullsFirst: false })
        .limit(1000);
      if (error) throw error;
      return (data as TaskRowWithClientJoin[]).map(flattenClient);
    },
  });
}

/**
 * Tarefas vinculadas a um cliente específico (todas, independente de assignee).
 * Útil dentro do contexto de um cliente.
 */
export function useTasksForClient(clientId: string | null) {
  return useQuery({
    queryKey: ['tasks', 'client', clientId],
    enabled: !!clientId,
    staleTime: 30_000,
    queryFn: async (): Promise<TaskWithClient[]> => {
      if (!clientId) return [];
      const { data, error } = await supabase
        .from('tasks')
        .select('*, client:clients(name)')
        .is('deleted_at', null)
        .eq('client_id', clientId)
        .order('due_at', { ascending: true, nullsFirst: false });
      if (error) throw error;
      return (data as TaskRowWithClientJoin[]).map(flattenClient);
    },
  });
}

export interface CreateTaskInput {
  title: string;
  description?: string | null;
  client_id?: string | null;
  assigned_to_user_id?: string | null;
  due_at?: string | null;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
}

export function useCreateTask() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: CreateTaskInput): Promise<Task> => {
      if (!user?.id) throw new Error('Não autenticado');
      const insert: TaskInsert = {
        title: input.title,
        description: input.description ?? null,
        client_id: input.client_id ?? null,
        assigned_to_user_id: input.assigned_to_user_id ?? user.id,
        due_at: input.due_at ?? null,
        priority: input.priority ?? 'normal',
        source: 'manual',
        created_by_user_id: user.id,
      };
      const { data, error } = await supabase.from('tasks').insert(insert).select('*').single();
      if (error) throw error;
      return data as Task;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Tarefa criada!');
    },
    onError: (e: Error) => toast.error('Erro ao criar tarefa: ' + e.message),
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: TaskUpdate }) => {
      const { error } = await supabase.from('tasks').update(updates).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (e: Error) => toast.error('Erro ao atualizar tarefa: ' + e.message),
  });
}

export function useCompleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('tasks')
        .update({ status: 'completed', completed_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Tarefa concluída!');
    },
    onError: (e: Error) => toast.error('Erro ao concluir tarefa: ' + e.message),
  });
}

export function useReopenTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('tasks')
        .update({ status: 'pending', completed_at: null })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Tarefa reaberta');
    },
    onError: (e: Error) => toast.error('Erro: ' + e.message),
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('tasks')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Tarefa excluída');
    },
    onError: (e: Error) => toast.error('Erro: ' + e.message),
  });
}
